import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CallButton from "../components/CallButton";
import { RefreshCw, Search, Filter, Globe, Compass, Tag, Phone, Mail, Wallet, UserPlus, Plus, MessageSquare, Send, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  StatCard, StatusDropdown, Pagination, Avatar, Select, Modal, toast, Spinner, Copyable,
} from "../components/ui";
import {
  LEAD_STATUSES, CLIENT_STATUSES, statusMeta, countryName, countryFlag, money,
} from "../lib/constants";

const LEAD_CARDS = [
  { key: "total", label: "Total Leads", accent: "text-indigo-600 dark:text-indigo-400" },
  { key: "new", label: "New", accent: "text-blue-600 dark:text-blue-400" },
  { key: "no_answer", label: "No Answer", accent: "text-amber-600 dark:text-amber-400" },
  { key: "not_interested", label: "Not Interested", accent: "text-rose-600 dark:text-rose-400" },
  { key: "wrong_information", label: "Wrong Info", accent: "text-orange-600 dark:text-orange-400" },
  { key: "call_back", label: "Call Back", accent: "text-cyan-600 dark:text-cyan-400" },
  { key: "investment", label: "Investment", accent: "text-emerald-600 dark:text-emerald-400" },
  { key: "no_language", label: "No Language", accent: "text-slate-500" },
];
const CLIENT_CARDS = [
  { key: "total", label: "Total Clients", accent: "text-indigo-600 dark:text-indigo-400" },
  { key: "in_progress", label: "In Progress", accent: "text-purple-600 dark:text-purple-400" },
  { key: "pending", label: "Pending", accent: "text-amber-600 dark:text-amber-400" },
  { key: "deposit", label: "Deposit", accent: "text-emerald-600 dark:text-emerald-400" },
  { key: "not_interested", label: "Not Interested", accent: "text-rose-600 dark:text-rose-400" },
  { key: "done", label: "Done", accent: "text-blue-600 dark:text-blue-400" },
  { key: "investment", label: "Investment", accent: "text-cyan-600 dark:text-cyan-400" },
];

// Last-used list filters per type, so leaving to a lead/client page and coming
// back restores the same view.
function loadSavedFilters(type) {
  try {
    const f = JSON.parse(localStorage.getItem(`crm-list-filters-${type}`));
    return f && typeof f === "object" ? f : {};
  } catch {
    return {};
  }
}

// Query string that carries the list's current filters onto the detail page,
// so prev/next there step through this same filtered list.
function listDetailQuery({ search, status, country, source, assigned }) {
  const p = new URLSearchParams();
  if (search) p.set("search", search);
  if (status) p.set("status", status);
  if (country) p.set("country", country);
  if (source) p.set("source", source);
  if (assigned) p.set("assigned", assigned);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export default function Contacts({ type }) {
  const isLead = type === "lead";
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const statuses = isLead ? LEAD_STATUSES : CLIENT_STATUSES;
  const cards = isLead ? LEAD_CARDS : CLIENT_CARDS;

  const [stats, setStats] = useState({ total: 0, by_status: {}, total_balance: 0 });
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const savedFilters = useMemo(() => loadSavedFilters(type), [type]);
  const [search, setSearch] = useState(savedFilters.search || "");
  const [debounced, setDebounced] = useState(savedFilters.search || "");
  const [statusFilter, setStatusFilter] = useState(savedFilters.status || "");
  const [countryFilter, setCountryFilter] = useState(savedFilters.country || "");
  const [sourceFilter, setSourceFilter] = useState(savedFilters.source || "");
  const [assignedFilter, setAssignedFilter] = useState(savedFilters.assigned || "");
  const [countries, setCountries] = useState([]);
  const [sources, setSources] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [assignModal, setAssignModal] = useState(false);
  const [moveSourceModal, setMoveSourceModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [commentFor, setCommentFor] = useState(null);
  const [quickText, setQuickText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debounced, statusFilter, countryFilter, sourceFilter, assignedFilter, type, pageSize]);

  useEffect(() => {
    try {
      localStorage.setItem(`crm-list-filters-${type}`, JSON.stringify({
        search, status: statusFilter, country: countryFilter,
        source: sourceFilter, assigned: assignedFilter,
      }));
    } catch { /* storage unavailable (e.g. private mode) */ }
  }, [type, search, statusFilter, countryFilter, sourceFilter, assignedFilter]);

  const loadStats = useCallback(async () => {
    const r = await api.get("/clients/stats", { params: { type } });
    setStats(r.data);
  }, [type]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients", {
        params: { type, status: statusFilter || undefined, country: countryFilter || undefined,
          source: sourceFilter || undefined, assigned: assignedFilter || undefined, search: debounced || undefined, page, page_size: pageSize },
      });
      setData(r.data);
    } finally { setLoading(false); }
  }, [type, statusFilter, countryFilter, sourceFilter, assignedFilter, debounced, page, pageSize]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    api.get("/clients/countries", { params: { type } }).then((r) => setCountries(r.data)).catch(() => {});
    api.get("/clients/sources", { params: { type } }).then((r) => setSources(r.data)).catch(() => {});
    if (isAdmin) api.get("/users/simple").then((r) => setAgents(r.data)).catch(() => {});
  }, [type, isAdmin]);

  const refresh = () => { loadStats(); loadList(); };

  const changeStatus = async (c, value) => {
    const field = isLead ? "lead_status" : "client_status";
    await api.put(`/clients/${c.id}`, { [field]: value });
    setData((d) => ({ ...d, items: d.items.map((x) => x.id === c.id ? { ...x, [field]: value } : x) }));
    loadStats();
    toast("Status updated");
  };

  const toggleSel = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const quickSelect = (n) => {
    setSelected(new Set(data.items.slice(0, n).map((x) => x.id)));
  };
  const selectAllPage = () => {
    const all = data.items.every((x) => selected.has(x.id));
    setSelected(all ? new Set() : new Set(data.items.map((x) => x.id)));
  };

  const statusValue = (c) => (isLead ? c.lead_status : c.client_status);

  const submitQuickComment = async (c) => {
    const content = quickText.trim();
    if (!content) return;
    setCommentBusy(true);
    try {
      await api.post(`/clients/${c.id}/comments`, { content, is_quick_comment: true });
      setCommentFor(null); setQuickText("");
      toast("Comment added");
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setCommentBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white" data-testid="page-title">
            {isLead ? "Leads" : "Clients"}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{isLead ? "Manage unanswered leads and follow-ups" : "Active clients responding to calls"}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isLead && (
            <div className="card px-4 py-2 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Total Balance</div>
                <div className="font-bold text-slate-900 dark:text-white font-display">{money(stats.total_balance)}</div>
              </div>
            </div>
          )}
          <button className="btn-secondary" onClick={refresh} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button className="btn-primary" onClick={() => setCreateModal(true)} data-testid="add-contact-btn"><Plus className="w-4 h-4" /> Add {isLead ? "Lead" : "Client"}</button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
        {cards.map((c) => (
          <StatCard
            key={c.key}
            label={c.label}
            accent={c.accent}
            value={(c.key === "total" ? stats.total : (stats.by_status?.[c.key] || 0)).toLocaleString()}
            active={c.key === "total" ? statusFilter === "" : statusFilter === c.key}
            onClick={() => setStatusFilter(c.key === "total" ? "" : c.key)}
            testid={`stat-${c.key}`}
          />
        ))}
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className="input pl-9" placeholder="Search by name, email, or phone..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
            <Select className="pl-9" value={statusFilter} onChange={setStatusFilter} placeholder="All Statuses" options={statuses.map((s) => ({ value: s.value, label: s.label }))} testid="status-filter" />
          </div>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
            <Select className="pl-9" value={countryFilter} onChange={setCountryFilter} placeholder="All Countries" options={countries.map((c) => ({ value: c.country, label: `${countryName(c.country)} (${c.count})` }))} testid="country-filter" />
          </div>
          <div className="relative">
            <Compass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
            <Select className="pl-9" value={sourceFilter} onChange={setSourceFilter} placeholder="All Sources" options={sources.map((s) => ({ value: s.name, label: `${s.name} (${s.count})` }))} testid="source-filter" />
          </div>
          {isAdmin ? (
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
              <Select className="pl-9" value={assignedFilter} onChange={setAssignedFilter} placeholder="All Agents"
                options={[{ value: "unassigned", label: "Unassigned" }, ...agents.map((a) => ({ value: a.id, label: a.full_name }))]} testid="agent-filter" />
            </div>
          ) : <div />}
        </div>

        {(search || statusFilter || countryFilter || sourceFilter || assignedFilter) && (
          <div className="flex justify-end -mt-2">
            <button className="btn-ghost text-xs px-2.5 py-1"
              onClick={() => { setSearch(""); setStatusFilter(""); setCountryFilter(""); setSourceFilter(""); setAssignedFilter(""); }}
              data-testid="clear-filters-btn">
              <X className="w-3.5 h-3.5" /> Clear all filters
            </button>
          </div>
        )}

        {/* Quick select + bulk */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500 dark:text-slate-400 mr-1">Quick select:</span>
          {[10, 20, 30, 50].map((n) => (
            <button key={n} className="btn-secondary px-3 py-1.5" onClick={() => quickSelect(n)} data-testid={`quick-${n}`}>{n}</button>
          ))}
          <button className="btn-secondary px-3 py-1.5" onClick={selectAllPage} data-testid="select-page">Select Page</button>
          {selected.size > 0 && (
            <span className="ml-1 text-slate-600 dark:text-slate-300">{selected.size} selected</span>
          )}
          {isAdmin && selected.size > 0 && (
            <>
              <button className="btn-primary px-3 py-1.5" onClick={() => setAssignModal(true)} data-testid="bulk-assign-btn"><UserPlus className="w-4 h-4" /> Assign</button>
              <button className="btn-secondary px-3 py-1.5" onClick={() => setMoveSourceModal(true)} data-testid="bulk-move-source-btn"><Compass className="w-4 h-4" /> Move to source</button>
              <button className="btn-secondary px-3 py-1.5" onClick={() => setSelected(new Set())} data-testid="clear-sel">Clear</button>
            </>
          )}
          <span className="ml-auto text-slate-500 dark:text-slate-400 flex items-center gap-2">
            Show:
            <select className="input h-8 w-20" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} data-testid="page-size">
              {[20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead className="bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <tr>
                <th className="p-3 w-10"><input type="checkbox" checked={data.items.length > 0 && data.items.every((x) => selected.has(x.id))} onChange={selectAllPage} data-testid="select-all-checkbox" /></th>
                <th className="p-3 text-left w-10">#</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Country</th>
                {!isLead && <th className="p-3 text-left">Balance</th>}
                <th className="p-3 text-left">Agent</th>
                <th className="p-3 text-left">Comment</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isLead ? 9 : 10} className="p-12 text-center text-slate-400"><Spinner className="w-5 h-5 inline" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={isLead ? 9 : 10} className="p-12 text-center text-slate-400">No {isLead ? "leads" : "clients"} found</td></tr>
              ) : data.items.map((c, i) => (
                <tr key={c.id} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition-colors cursor-pointer"
                  onClick={() => nav(`/${type}s/${c.id}${listDetailQuery({ search: debounced, status: statusFilter, country: countryFilter, source: sourceFilter, assigned: assignedFilter })}`)} data-testid={`row-${c.id}`}>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} data-testid={`select-${c.id}`} /></td>
                  <td className="p-3 text-slate-400">{(page - 1) * pageSize + i + 1}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={c.name} size="h-8 w-8" />
                      <span className="font-medium text-slate-800 dark:text-slate-100">{c.name}</span>
                    </div>
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <StatusDropdown type={type} value={statusValue(c)} options={statuses} onChange={(v) => changeStatus(c, v)} testid={`status-${c.id}`} />
                  </td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    {c.phone ? (
                      <div className="flex items-center gap-1">
                        <Copyable value={c.phone} testid={`copy-phone-${c.id}`} className="text-indigo-600 dark:text-indigo-400">
                          <span className="whitespace-nowrap">{c.phone}</span>
                        </Copyable>
                        <CallButton phone={c.phone} clientId={c.id} title="Call"
                          className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-700/60"
                          testid={`call-${c.id}`}><Phone className="w-3.5 h-3.5" /></CallButton>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-3 text-slate-600 dark:text-slate-300 max-w-[180px] truncate">
                    {c.email ? <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" />{c.email}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="p-3 whitespace-nowrap"><span className="text-base mr-1">{countryFlag(c.country)}</span><span className="text-slate-600 dark:text-slate-300">{countryName(c.country)}</span></td>
                  {!isLead && <td className="p-3 font-medium text-emerald-600 dark:text-emerald-400">{money(c.balance)}</td>}
                  <td className="p-3 text-slate-600 dark:text-slate-300">{c.assigned_agent_name || <span className="text-slate-300">Unassigned</span>}</td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    {commentFor === c.id ? (
                      <div className="flex items-center gap-1">
                        <input autoFocus className="input h-8 flex-1 min-w-[140px]" placeholder="Quick comment..." value={quickText}
                          onChange={(e) => setQuickText(e.target.value)}
                          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") submitQuickComment(c); else if (e.key === "Escape") setCommentFor(null); }}
                          data-testid={`quick-comment-${c.id}`} />
                        <button className="btn-primary p-1.5" disabled={commentBusy} onClick={() => submitQuickComment(c)} data-testid={`quick-comment-submit-${c.id}`}><Send className="w-3.5 h-3.5" /></button>
                        <button className="btn-ghost p-1.5 text-slate-400" onClick={() => setCommentFor(null)} data-testid={`quick-comment-cancel-${c.id}`}><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <button className="btn-ghost p-1.5 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400" title="Quick comment"
                        onClick={() => { setCommentFor(c.id); setQuickText(""); }} data-testid={`quick-comment-btn-${c.id}`}>
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 border-t border-slate-100 dark:border-slate-800">
          <Pagination page={page} pageSize={pageSize} total={data.total} onPage={setPage} />
        </div>
      </div>

      {assignModal && (
        <AssignModal agents={agents} selected={[...selected]} onClose={() => setAssignModal(false)} onDone={() => { setAssignModal(false); setSelected(new Set()); refresh(); }} />
      )}
      {moveSourceModal && (
        <MoveSourceModal sources={sources} selected={[...selected]} onClose={() => setMoveSourceModal(false)} onDone={() => { setMoveSourceModal(false); setSelected(new Set()); refresh(); }} />
      )}
      {createModal && (
        <CreateModal type={type} agents={agents} isAdmin={isAdmin} onClose={() => setCreateModal(false)} onDone={() => { setCreateModal(false); refresh(); }} />
      )}
    </div>
  );
}

function AssignModal({ agents, selected, onClose, onDone }) {
  const [agentId, setAgentId] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/clients/bulk-assign", { client_ids: selected, agent_id: agentId || null });
      toast(`${selected.length} contacts updated`);
      onDone();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Assign ${selected.length} contacts`} testid="assign-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy} data-testid="assign-submit">Apply</button></>}>
      <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Assign to agent</label>
      <Select value={agentId} onChange={setAgentId} placeholder="Unassign" options={agents.map((a) => ({ value: a.id, label: a.full_name }))} testid="assign-agent-select" />
    </Modal>
  );
}

function MoveSourceModal({ sources, selected, onClose, onDone }) {
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!source) return toast("Pick a source", "error");
    setBusy(true);
    try {
      const r = await api.post("/clients/bulk-move-source", { client_ids: selected, source });
      toast(`Moved ${r.data.modified} lead${r.data.modified === 1 ? "" : "s"} to ${source}`);
      onDone();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Move ${selected.length} contact${selected.length === 1 ? "" : "s"} to source`} testid="move-source-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy || !source} data-testid="move-source-submit">Move</button></>}>
      <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Move to source</label>
      <Select value={source} onChange={setSource} placeholder="Select a source..." options={sources.map((s) => ({ value: s.name, label: s.name }))} testid="move-source-select" />
    </Modal>
  );
}

function CreateModal({ type, agents, isAdmin, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", assigned_agent_id: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name) return toast("Name is required", "error");
    setBusy(true);
    try {
      await api.post("/clients", { ...form, client_type: type, assigned_agent_id: form.assigned_agent_id || null });
      toast(`${type === "lead" ? "Lead" : "Client"} created`);
      onDone();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Add ${type === "lead" ? "Lead" : "Client"}`} testid="create-modal"
      footer={<><button className="btn-secondary" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={submit} disabled={busy} data-testid="create-submit">Create</button></>}>
      {["name", "email", "phone"].map((k) => (
        <div key={k}>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300 capitalize">{k}</label>
          <input className="input mt-1" value={form[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`create-${k}`} />
        </div>
      ))}
      <div>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Country code (e.g. GB)</label>
        <input className="input mt-1" value={form.country} onChange={(e) => upd("country", e.target.value.toUpperCase())} maxLength={2} data-testid="create-country" />
      </div>
      {isAdmin && (
        <div>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Assign agent</label>
          <Select value={form.assigned_agent_id} onChange={(v) => upd("assigned_agent_id", v)} placeholder="Unassigned" options={agents.map((a) => ({ value: a.id, label: a.full_name }))} testid="create-agent" />
        </div>
      )}
    </Modal>
  );
}
